import { supabase } from "@/integrations/supabase/client";
import {
  defaultServiceSpec,
  resolveLinkPlan,
  type IndustryLinkPlan,
  type LinkPlanSpec,
} from "@/lib/internal-links";

export type LinkGroup = "articles" | "services" | "calculators" | "templates";

export const LINK_GROUPS: LinkGroup[] = ["articles", "services", "calculators", "templates"];

export const GROUP_LABEL: Record<LinkGroup, string> = {
  articles: "Articles",
  services: "Services",
  calculators: "Calculators",
  templates: "Templates",
};

export interface ServiceLinkPlanRecord {
  service_slug: string;
  spec: LinkPlanSpec;
  updated_at: string;
}

/** Narrow unknown JSON from the database into a link plan spec. */
export function parseSpec(value: unknown): LinkPlanSpec {
  const spec: LinkPlanSpec = {};
  if (!value || typeof value !== "object") return spec;
  for (const group of LINK_GROUPS) {
    const raw = (value as Record<string, unknown>)[group];
    if (!Array.isArray(raw)) continue;
    spec[group] = raw
      .map((entry) =>
        Array.isArray(entry) && typeof entry[0] === "string"
          ? ([entry[0], typeof entry[1] === "string" ? entry[1] : ""] as [string, string])
          : null
      )
      .filter((e): e is [string, string] => e !== null);
  }
  return spec;
}

/** Fetch the CMS override for one service page. Returns null when none is saved. */
export async function fetchServiceLinkPlanSpec(serviceSlug: string): Promise<LinkPlanSpec | null> {
  const { data, error } = await supabase
    .from("service_link_plans")
    .select("spec")
    .eq("service_slug", serviceSlug)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return parseSpec(data.spec);
}

/** Fetch every saved override, keyed by service slug. */
export async function fetchAllServiceLinkPlanSpecs(): Promise<Record<string, LinkPlanSpec>> {
  const { data, error } = await supabase.from("service_link_plans").select("service_slug, spec");
  if (error) throw error;
  const out: Record<string, LinkPlanSpec> = {};
  for (const row of data ?? []) out[row.service_slug] = parseSpec(row.spec);
  return out;
}

/** Save (or replace) the override for a service page. Requires an admin/editor session. */
export async function saveServiceLinkPlanSpec(serviceSlug: string, spec: LinkPlanSpec) {
  const { data: userData } = await supabase.auth.getUser();
  const { error } = await supabase.from("service_link_plans").upsert(
    {
      service_slug: serviceSlug,
      spec: spec as never,
      updated_by: userData.user?.id ?? null,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "service_slug" }
  );
  if (error) throw error;
}

/** Remove the override so the page falls back to the code-managed plan. */
export async function deleteServiceLinkPlanSpec(serviceSlug: string) {
  const { error } = await supabase.from("service_link_plans").delete().eq("service_slug", serviceSlug);
  if (error) throw error;
}

/** Resolve the plan a service page should render, preferring the CMS override. */
export function resolveServicePlan(serviceSlug: string, override: LinkPlanSpec | null): IndustryLinkPlan {
  const spec = override && Object.keys(override).length > 0 ? override : defaultServiceSpec(serviceSlug);
  return resolveLinkPlan(spec, serviceSlug);
}
